import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type { Denormalized } from './parser';
import { createOne, deleteOne, fetchList, fetchOne, updateOne, type CreateBody, type UpdateBody } from './client';
import type { ListQuery } from './types';
import { getActiveTenantOverride } from '@/shared/lib/active-tenant';

export const queryKeys = {
  list: (type: string, query?: ListQuery, tenantScope?: string | null) =>
    ['jsonapi', type, 'list', query ?? null, tenantScope ?? null] as const,
  one: (type: string, id: string, query?: Pick<ListQuery, 'include' | 'fields'>) =>
    ['jsonapi', type, 'one', id, query ?? null] as const,
};

export function useResourceList<T extends Denormalized = Denormalized>(
  type: string,
  query?: ListQuery,
  options?: Omit<UseQueryOptions<{ data: T[]; total: number; meta: Record<string, unknown> }>, 'queryKey' | 'queryFn'>
) {
  // Cache separata per tenant override: tornando sul proprio tenant la query è già cachata
  // e non rifetcha. Il refetch al cambio override è comunque forzato da invalidateQueries()
  // in AuthProvider.setCrossTenantOverride.
  const tenantScope = getActiveTenantOverride();
  return useQuery({
    queryKey: queryKeys.list(type, query, tenantScope),
    queryFn: () => fetchList<T>(type, query),
    ...options,
  });
}

export function useResource<T extends Denormalized = Denormalized>(
  type: string,
  id: string | undefined,
  query?: Pick<ListQuery, 'include' | 'fields'>,
  options?: Omit<UseQueryOptions<T | null>, 'queryKey' | 'queryFn' | 'enabled'>
) {
  return useQuery({
    queryKey: queryKeys.one(type, id ?? '', query),
    queryFn: () => fetchOne<T>(type, id as string, query),
    enabled: Boolean(id),
    ...options,
  });
}

export function useCreateResource<T extends Denormalized = Denormalized>(type: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBody) => createOne<T>(type, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jsonapi', type, 'list'] });
    },
  });
}

export function useUpdateResource<T extends Denormalized = Denormalized>(type: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateBody) => updateOne<T>(type, body),
    onSuccess: (_, body) => {
      qc.invalidateQueries({ queryKey: ['jsonapi', type, 'list'] });
      qc.invalidateQueries({ queryKey: ['jsonapi', type, 'one', body.id] });
    },
  });
}

export function useDeleteResource(type: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteOne(type, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jsonapi', type] });
    },
  });
}
